import type { Framework } from "./types";

export const frameworks: Framework[] = [
  {
    slug: "ai-sustainability-paradox",
    name: "The AI–Sustainability Paradox",
    shortDescription:
      "A framework for reading claims about AI's role in sustainability by forcing them onto a single, auditable ledger.",
    coreQuestion:
      "What does the net effect of a given AI system look like once its downstream gains and its infrastructure costs are booked against the same accounting boundary?",
    explanation:
      "The framework treats every AI system as a single object with two attributable emissions streams: the infrastructure that runs it (training, inference, cooling, embodied hardware) and the change it produces in the sectors it serves (efficiency gains, avoided emissions, induced demand). It insists that both streams appear on the same ledger before any ‘net’ claim is made. The framework is not a verdict on AI; it is a discipline for evaluating specific deployments, and its usefulness comes from making certain kinds of rhetorical moves — advocating gains without costs, or costs without gains — visible as omissions rather than arguments.",
    components: [
      {
        name: "Infrastructure Ledger",
        description:
          "All emissions and material flows attributable to running the system: training energy, inference energy, embodied hardware footprint, cooling water, siting-related grid effects.",
      },
      {
        name: "Downstream Ledger",
        description:
          "Measured, not modelled, changes in the sectors the system serves — energy, materials, waste, land use — attributable to its deployment.",
      },
      {
        name: "Rebound Term",
        description:
          "Induced demand and displacement effects: efficiency gains that lower unit cost and expand consumption, or that shift load to less-visible actors.",
      },
      {
        name: "Accounting Boundary",
        description:
          "The explicit statement of whose books close the ledger. Without an owner of the boundary, gains and costs remain on separate documents that never sum.",
      },
    ],
    useCases: [
      "Regulatory review of ‘AI for climate’ programs seeking to count toward emissions targets.",
      "Corporate disclosure design for AI-enabled products claiming sustainability benefits.",
      "Grant portfolio evaluation for climate-tech funds deploying AI-heavy interventions.",
      "Journalistic evaluation of vendor claims that lack an infrastructure line item.",
    ],
    relatedConcepts: ["ai-sustainability-paradox", "efficiency-debt", "institutional-blindness"],
    relatedIdeas: ["can-ai-solve-the-climate-crisis", "the-energy-paradox-of-artificial-intelligence"],
    references: [
      { label: "IEA — Energy and AI (2025)", url: "https://www.iea.org/reports/energy-and-ai" },
      { label: "UNEP — State of Finance for Nature (2026)", url: "https://www.unep.org/resources/state-finance-nature" },
    ],
    topics: ["AI", "Sustainability", "Systems Thinking", "Governance"],
    updated: "2026-07-06",
  },
  {
    slug: "washing-economy-test",
    name: "The Washing Economy Test",
    shortDescription:
      "A four-part check for reading corporate transformation claims — green, AI, judgment, upskill, narrative — against the cost of the action they name.",
    coreQuestion:
      "Is this claim priced against the action it invokes, or is it priced against the appearance of that action?",
    explanation:
      "The washing economy — greenwashing, AI-washing, judgment-washing, upskill-washing, narrative-washing — is not a set of individual moral failures but a systemic mode of production: an equilibrium in which language is cheap and action is expensive, and legitimacy is manufactured at the language layer. The test forces a claim onto a ledger that names both sides. It is designed for regulators, journalists, and boards evaluating transformation announcements — including their own.",
    components: [
      {
        name: "The Cost of the Named Action",
        description:
          "The capex, opex, headcount, or capability cost of the transformation the claim describes, stated in units that appear in a budget.",
      },
      {
        name: "The Cost of the Announcement",
        description:
          "The marketing, communications, and consultancy cost of producing and defending the claim itself. When this line exceeds the previous one materially, the claim is doing rhetorical work, not transformational work.",
      },
      {
        name: "The Verification Path",
        description:
          "The independent measurement mechanism — auditor, third-party benchmark, disclosure standard — that would falsify the claim. A claim without a falsification path is not a strategy; it is a message.",
      },
      {
        name: "The Revision Rule",
        description:
          "The pre-committed protocol for revising the target when reality diverges. Without a revision rule, misses are absorbed as narrative rather than accounted for as debt.",
      },
    ],
    useCases: [
      "Regulatory review of net-zero, biodiversity and 'responsible AI' claims.",
      "Journalistic evaluation of transformation announcements from firms and governments.",
      "Board-level reading of internal reskilling, ESG, and AI-safety programmes before external launch.",
      "Investor due diligence on ESG-labelled products.",
    ],
    relatedConcepts: ["washing-economy", "commitment-debt", "ai-sustainability-paradox"],
    relatedIdeas: [
      "the-sustainability-fetish-a-critical-perspective",
      "what-is-sustainability-a-critique",
    ],
    references: [
      { label: "UNEP — Emissions Gap Report", url: "https://www.unep.org/resources/emissions-gap-report-2025" },
      { label: "Net Zero Tracker — Net Zero Stocktake", url: "https://zerotracker.net/analysis/net-zero-stocktake-2024" },
    ],
    topics: ["Sustainability", "Governance", "AI", "Ethics"],
    updated: "2026-07-06",
  },
  {
    slug: "commitment-debt-audit",
    name: "Commitment Debt Audit",
    shortDescription:
      "A framework for reading COP outcomes, corporate net-zero plans, and ESG pledges as an accumulating balance sheet rather than a series of announcements.",
    coreQuestion:
      "If we treat every pledge as a promissory note, what is the outstanding balance — and who is the counterparty entitled to enforce it?",
    explanation:
      "Commitment debt behaves like carbon debt: the balance grows, interest accrues, and a new note is written before payment day arrives. The difference is that carbon debt has a physical counterparty — the atmosphere — while commitment debt has a diffuse one, the public whose attention shifts to the next pledge. The audit forces four items into the same document: the pledge, its baseline, its revision rule, and the party entitled to enforce it. A pledge that lacks any of the four is not a target; it is a headline.",
    components: [
      {
        name: "The Pledge Stock",
        description:
          "All outstanding commitments made by the entity — net-zero, 2030 target, nature-positive, resource-reduction — enumerated as a single list rather than a rolling narrative.",
      },
      {
        name: "The Baseline",
        description:
          "The explicit reference year, scope boundary, and measurement standard against which the pledge is evaluated. A drifting baseline is a debt restructuring, not a target.",
      },
      {
        name: "The Revision Rule",
        description:
          "The pre-committed protocol for revising the pledge — interim milestones, escalation triggers, and the criteria that would count as a miss. Without this, a miss is absorbed as narrative.",
      },
      {
        name: "The Counterparty",
        description:
          "The party entitled to enforce the pledge — regulator, standard-setter, board committee, independent auditor. If no counterparty exists, the pledge is denominated in reputation, not accountability.",
      },
    ],
    useCases: [
      "COP outcome reviews across cycles rather than within a single summit.",
      "Corporate net-zero disclosure design that closes the audit gap between pledge and capex.",
      "Sovereign NDC updates evaluated as a debt schedule, not a fresh announcement.",
      "ESG fund methodology reviews focused on additionality and verification, not label count.",
    ],
    relatedConcepts: ["commitment-debt", "washing-economy", "efficiency-debt"],
    relatedIdeas: ["can-ai-solve-the-climate-crisis"],
    references: [
      { label: "UNEP — Emissions Gap Report", url: "https://www.unep.org/resources/emissions-gap-report-2025" },
      { label: "Net Zero Tracker", url: "https://zerotracker.net/" },
      { label: "Climate Action Tracker", url: "https://climateactiontracker.org/" },
    ],
    topics: ["Climate", "Sustainability", "Governance"],
    updated: "2026-07-06",
  },
];

export const frameworkBySlug = (slug: string): Framework | undefined =>
  frameworks.find((f) => f.slug === slug);
